import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Table } from "react-bootstrap";
import API from "../api";
import { useAuth } from "../context/AuthContext";

export default function SubAssignmentManagerPage() {
  const { user } = useAuth();
  const [players, setPlayers] = useState([]);
  const [subs, setSubs] = useState([]);        // sub assignments
  const [realSubs, setRealSubs] = useState([]); // available subs to pick from
  const [weeks, setWeeks] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingSub, setEditingSub] = useState(null);
  const [form, setForm] = useState({
    leagueid: "",
    weekid: "",
    playerid: "",
    subid: ""
  });

  const fetchPlayers = async () => {
    try {
      const res = await API.get(`/scorecard/players/${user.leagueid}`);
      setPlayers(res.data);
    } catch (err) {
      console.error("Failed to fetch players", err);
    }
  };

  const fetchSubs = async () => {
    try {
      const res = await API.get(`/scorecard/subs/${user.leagueid}`);
      setSubs(res.data);
    } catch (err) {
      console.error("Failed to fetch subs", err);
    }
  };

  const fetchRealSubs = async () => {
    try {
      const res = await API.get(`/scorecard/available-subs/${user.leagueid}`);
      setRealSubs(res.data);
    } catch (err) {
      console.error("Failed to fetch available subs", err);
    }
  };

  const fetchWeeks = async () => {
    try {
      if (!user?.leagueid) return;
      const res = await API.get(`/league/${user.leagueid}/weeks`);
      setWeeks(res.data);
    } catch (err) {
      console.error("Failed to fetch weeks", err);
    }
  };

  useEffect(() => {
    fetchPlayers();
    fetchSubs();
    fetchRealSubs();
    fetchWeeks();
  }, []);

  const handleOpenModal = (sub = null) => {
    if (sub) {
      setEditingSub(sub);
      setForm({
        leagueid: user.leagueid,
        weekid: sub.weekid || "",
        playerid: sub.playerid || "",
        subid: sub.subid || ""
      });
    } else {
      setEditingSub(null);
      setForm({
        leagueid: user.leagueid,
        weekid: "",
        playerid: "",
        subid: ""
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    try {
      const payload = {
        ...form,
        weekid: form.weekid ? parseInt(form.weekid) : null,
        playerid: form.playerid ? parseInt(form.playerid) : null,
        subid: form.subid ? parseInt(form.subid) : null,
        leagueid: user.leagueid,
      };

      if (editingSub) {
        await API.put(`/scorecard/subs/${editingSub.id}`, payload);
      } else {
        await API.post("/scorecard/subs", payload);
      }

      await fetchSubs();
      setShowModal(false);
    } catch (err) {
      console.error("Failed to save sub assignment", err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this sub assignment?")) return;
    try {
      await API.delete(`/scorecard/subs/${id}`);
      await fetchSubs();
    } catch (err) {
      console.error("Failed to delete sub assignment", err);
    }
  };

  return (
    <div className="container mt-4">
      <h3>Manage Sub Assignments</h3>
      <Button className="mb-3" onClick={() => handleOpenModal()}>Add Sub Assignment</Button>

      <Table striped bordered hover>
        <thead>
          <tr>
            <th>Week</th>
            <th>Player</th>
            <th>Sub</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {subs.map(assign => (
            <tr key={assign.id}>
              <td>{assign.week_number ? `Week ${assign.week_number} - ${assign.week_date}` : ""}</td>
              <td>{assign.player_name}</td>
              <td>{assign.sub_name}</td>
              <td>
                <Button variant="info" size="sm" onClick={() => handleOpenModal(assign)}>Edit</Button>{" "}
                <Button variant="danger" size="sm" onClick={() => handleDelete(assign.id)}>Delete</Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>{editingSub ? "Edit Sub Assignment" : "Add Sub Assignment"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Control type="hidden" name="leagueid" value={form.leagueid} />

            <Form.Group controlId="weekid" className="mb-2">
              <Form.Label>Week</Form.Label>
              <Form.Select
                name="weekid"
                value={form.weekid}
                onChange={handleChange}
              >
                <option value="">Select Week</option>
                {weeks.map((week) => (
                  <option key={week.id} value={week.id}>
                    {`Week ${week.week_number} - ${new Date(week.date).toLocaleDateString()}`}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group controlId="playerid" className="mb-2">
              <Form.Label>Player</Form.Label>
              <Form.Select
                name="playerid"
                value={form.playerid}
                onChange={handleChange}
              >
                <option value="">Select Player</option>
                {players.map(player => (
                  <option key={player.id} value={player.id}>
                    {player.firstname} {player.lastname}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group controlId="subid" className="mb-2">
              <Form.Label>Sub</Form.Label>
              <Form.Select
                name="subid"
                value={form.subid}
                onChange={handleChange}
              >
                <option value="">Select Sub</option>
                {realSubs.map(sub => (
                  <option key={sub.id} value={sub.id}>
                    {sub.firstname} {sub.lastname}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>Cancel</Button>
          <Button variant="primary" onClick={handleSave}>
            {editingSub ? "Update" : "Create"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
